import type { InvitationGuestRequirements } from '@/modules/auth/use-cases'
import type { RegistrationFieldIssue } from '@/modules/auth/domain'
import type { JoinFormValues } from './components/form-values'
import type { JoinStepId } from './join-step-types'
import { optionalFieldsOf } from '@/shared/domain'

export type ValidateFormValuesParams = {
  values: JoinFormValues
  requirements: InvitationGuestRequirements
  tosAccepted: boolean
  privacyAccepted: boolean
  legalAlreadyAccepted: boolean
  waiverComplete: boolean
  phoneLocked?: boolean
  skippedSteps?: JoinStepId[]
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const ZIP_PATTERN_US = /^\d{5}(-\d{4})?$/
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/

export const STEP_FIELD_PREFIXES: Record<JoinStepId, string[]> = {
  personal_info: ['firstName', 'lastName', 'email', 'phone', 'flywayRegion', 'legalDocuments'],
  date_of_birth: ['dateOfBirth'],
  address: ['addressLine', 'city', 'state', 'zipCode', 'country'],
  emergency_contacts: ['emergency'],
  hunting_licenses: ['licenseEntries.'],
  vehicle_info: ['vehicle'],
  drivers_license: ['driversLicense'],
  liability_waiver: ['liabilityWaiver'],
}

const SERVER_FIELD_PREFIX = 'userData.'

const FORM_FIELD_NAMES: ReadonlySet<string> = new Set(
  Object.values(STEP_FIELD_PREFIXES)
    .flat()
    .filter((prefix) => !prefix.endsWith('.')),
)

export function mapServerIssuesToFormErrors(
  issues: RegistrationFieldIssue[],
): Record<string, string> {
  const errors: Record<string, string> = {}

  for (const issue of issues) {
    const key = issue.field.startsWith(SERVER_FIELD_PREFIX)
      ? issue.field.slice(SERVER_FIELD_PREFIX.length)
      : issue.field
    if (key === '' || key.includes('.')) continue
    if (!isMappableFormField(key)) continue
    if (errors[key] === undefined) errors[key] = issue.message
  }

  return errors
}

function isMappableFormField(key: string): boolean {
  if (FORM_FIELD_NAMES.has(key)) return true
  return Object.values(STEP_FIELD_PREFIXES)
    .flat()
    .some((prefix) => !prefix.endsWith('.') && key.startsWith(prefix))
}

function isPresent(value?: string): value is string {
  return typeof value === 'string' && value.trim().length > 0
}

export function countryHasStateOptions(country?: string): boolean {
  return country === 'US' || country === 'CA'
}

export function validateFormValues({
  values,
  requirements,
  tosAccepted,
  privacyAccepted,
  legalAlreadyAccepted,
  waiverComplete,
  phoneLocked,
  skippedSteps,
}: ValidateFormValuesParams): Record<string, string> {
  const errors: Record<string, string> = {}

  if (!isPresent(values.firstName)) errors.firstName = 'First name is required'
  if (!isPresent(values.lastName)) errors.lastName = 'Last name is required'
  if (!isPresent(values.email)) {
    errors.email = 'Email is required'
  } else if (!EMAIL_PATTERN.test(values.email)) {
    errors.email = 'Enter a valid email address'
  }

  if (!phoneLocked) {
    if (!isPresent(values.phone)) {
      errors.phone = 'Phone number is required'
    } else {
      const phoneDigits = values.phone.replace(/\D/g, '')
      if (!values.phone.startsWith('+')) {
        errors.phone = 'Phone must be in international format (e.g., +15551234567)'
      } else if (phoneDigits.length < 11) {
        errors.phone = 'Please enter a complete phone number'
      }
    }
  }

  if (!isPresent(values.flywayRegion)) errors.flywayRegion = 'Select a flyway region'

  if (requirements.basic_info?.date_of_birth?.required && !isPresent(values.dateOfBirth)) {
    errors.dateOfBirth = 'Date of birth is required'
  }

  if (!legalAlreadyAccepted && (!tosAccepted || !privacyAccepted)) {
    errors.legalDocuments = 'You must accept the Terms of Service and Privacy Policy'
  }

  if (requirements.address) {
    if (!isPresent(values.addressLine1)) errors.addressLine1 = 'Street address is required'
    if (!isPresent(values.city)) errors.city = 'City is required'
    if (!isPresent(values.country)) errors.country = 'Country is required'
    if (countryHasStateOptions(values.country) && !isPresent(values.state)) {
      errors.state = 'State is required'
    }
    if (!isPresent(values.zipCode)) {
      errors.zipCode = 'ZIP code is required'
    } else if (values.country === 'US' && !ZIP_PATTERN_US.test(values.zipCode)) {
      errors.zipCode =
        'Enter a valid 5-digit ZIP code, or change Country if you are not in the United States'
    }
  }

  const optionalFields = optionalFieldsOf(
    requirements as unknown as Record<string, unknown>,
  )
  const contactIsRequired = (key: string): boolean => !optionalFields.includes(key)

  if (requirements.emergency_contact_1 && contactIsRequired('emergency_contact_1')) {
    if (!isPresent(values.emergencyContact)) {
      errors.emergencyContact = 'Emergency contact name is required'
    }
    if (!isPresent(values.emergencyPhone)) {
      errors.emergencyPhone = 'Emergency contact phone is required'
    }
  }

  if (requirements.emergency_contact_2 && contactIsRequired('emergency_contact_2')) {
    if (!isPresent(values.emergencyContact2)) {
      errors.emergencyContact2 = 'Second emergency contact name is required'
    }
    if (!isPresent(values.emergencyPhone2)) {
      errors.emergencyPhone2 = 'Second emergency contact phone is required'
    }
  }

  if (requirements.required_license_types?.length) {
    for (const licenseType of requirements.required_license_types) {
      const entry = values.licenseEntries?.[licenseType]
      if (!entry?.exempt && !isPresent(entry?.licenseNumber)) {
        errors[`licenseEntries.${licenseType}.licenseNumber`] = 'License number is required'
      }
      if (!entry?.exempt && isPresent(entry?.expiry) && !DATE_PATTERN.test(entry.expiry.trim())) {
        errors[`licenseEntries.${licenseType}.expiry`] = 'Enter a valid date'
      }
    }
  }

  if (requirements.vehicle_info) {
    if (!isPresent(values.vehicleMake)) errors.vehicleMake = 'Vehicle make is required'
    if (!isPresent(values.vehicleModel)) errors.vehicleModel = 'Vehicle model is required'
    if (!isPresent(values.vehicleColor)) errors.vehicleColor = 'Vehicle color is required'
    if (!isPresent(values.vehicleLicensePlate)) {
      errors.vehicleLicensePlate = 'License plate is required'
    }
    if (!isPresent(values.vehicleState)) errors.vehicleState = 'Plate state is required'
  }

  if (requirements.driversLicense) {
    const driversLicense = values.driversLicense
    if (!isPresent(driversLicense?.issuingState)) {
      errors.driversLicenseIssuingState = 'Issuing state is required'
    }
    if (!isPresent(driversLicense?.documentNumber)) {
      errors.driversLicenseNumber = 'Licence number is required'
    }
    if (!isPresent(driversLicense?.fileBase64)) {
      errors.driversLicenseFile = 'A photo of your driver\u2019s licence is required'
    }
    if (isPresent(driversLicense?.expiry) && !DATE_PATTERN.test(driversLicense.expiry)) {
      errors.driversLicenseExpiry = 'Enter a valid date'
    }
  }

  if (requirements.liability_waiver === true && !waiverComplete) {
    errors.liabilityWaiver = 'Please sign the required liability waiver to continue'
  }

  if (skippedSteps?.length) {
    for (const stepId of skippedSteps) {
      const prefixes = STEP_FIELD_PREFIXES[stepId]
      for (const key of Object.keys(errors)) {
        if (prefixes.some((prefix) => key.startsWith(prefix))) {
          delete errors[key]
        }
      }
    }
  }

  return errors
}

export function validateStepFields(
  params: ValidateFormValuesParams & { stepId: JoinStepId },
): Record<string, string> {
  const allErrors = validateFormValues(params)
  const prefixes = STEP_FIELD_PREFIXES[params.stepId]
  const filtered: Record<string, string> = {}
  for (const key of Object.keys(allErrors)) {
    if (prefixes.some((prefix) => key.startsWith(prefix))) {
      filtered[key] = allErrors[key]
    }
  }
  return filtered
}

export const COMPLIANCE_FIELD_TO_STEP: Record<string, JoinStepId> = {
  date_of_birth: 'date_of_birth',
  address: 'address',
  emergency_contact_1: 'emergency_contacts',
  emergency_contact_2: 'emergency_contacts',
  vehicle_info: 'vehicle_info',
  driversLicense: 'drivers_license',
}

const COMPLIANCE_FALLBACK_STEP: JoinStepId = 'hunting_licenses'

export function mapComplianceMissingFieldsToSteps(missingFields: string[]): JoinStepId[] {
  const stepIds: JoinStepId[] = []
  for (const field of missingFields) {
    const stepId = COMPLIANCE_FIELD_TO_STEP[field] ?? COMPLIANCE_FALLBACK_STEP
    if (!stepIds.includes(stepId)) stepIds.push(stepId)
  }
  return stepIds
}
